import { SchemaOf, boolean, mixed, object, string } from 'yup';

import { AzureAuthMethod, BackupAzureSettings } from './types';
import { CRON_RULE_REGEX } from './cron-validation';

const principalMethods: AzureAuthMethod[] = [
  'servicePrincipalSecret',
  'servicePrincipalCertificate',
];

export function validationSchema(): SchemaOf<BackupAzureSettings> {
  return object({
    scheduleAutomaticBackup: boolean().default(false),

    cronRule: string()
      .default('')
      .when('scheduleAutomaticBackup', {
        is: true,
        then: (schema) =>
          schema.required('This field is required.').when('cronRule', {
            is: (val: string) => val !== '',
            then: (s) =>
              s.matches(CRON_RULE_REGEX, 'Please enter a valid cron rule.'),
          }),
      }),

    // Always required — users need these regardless of whether scheduling is on
    storageAccountName: string()
      .default('')
      .required('This field is required.'),

    containerName: string().default('').required('This field is required.'),

    authMethod: mixed<AzureAuthMethod>()
      .oneOf([
        'servicePrincipalSecret',
        'servicePrincipalCertificate',
        'managedIdentity',
        'storageAccountKey',
      ])
      .default('servicePrincipalSecret') as SchemaOf<AzureAuthMethod>,

    // Service Principal shared fields — required when a principal method is selected
    tenantId: string()
      .default('')
      .when('authMethod', {
        is: (method: AzureAuthMethod) => principalMethods.includes(method),
        then: (schema) => schema.required('This field is required.'),
      }),

    clientId: string()
      .default('')
      .when('authMethod', {
        is: (method: AzureAuthMethod) => principalMethods.includes(method),
        then: (schema) => schema.required('This field is required.'),
      }),

    clientSecret: string()
      .default('')
      .when('authMethod', {
        is: 'servicePrincipalSecret',
        then: (schema) => schema.required('This field is required.'),
      }),

    clientCertificate: string()
      .default('')
      .when('authMethod', {
        is: 'servicePrincipalCertificate',
        then: (schema) => schema.required('This field is required.'),
      }),

    clientCertificatePassword: string().default('').optional(),

    managedIdentityClientId: string().default('').optional(),

    storageAccountKey: string()
      .default('')
      .when('authMethod', {
        is: 'storageAccountKey',
        then: (schema) => schema.required('This field is required.'),
      }),

    serviceUrl: string().default('').optional(),

    passwordProtect: boolean().default(false),
    password: string()
      .default('')
      .when('passwordProtect', {
        is: true,
        then: (schema) => schema.required('This field is required.'),
      }),
  });
}
